/**
 * How wide the desk's working area is, and the handle that changes it.
 *
 * The map fills whatever the working area leaves, so this is the one number that decides
 * how much of the screen is queue and how much is cars. It used to be 900px and nothing
 * else; a dispatcher with a long queue wanted the map smaller, and one covering a big rank
 * wants it bigger, so the divider between the two drags. The floor is the old fixed width,
 * because below it the telephone column and the queue start fighting over pixels; the
 * ceiling leaves the map enough to be a map.
 *
 * Remembered on the person's membership, so it follows them to the next desk; the browser
 * keeps a copy so the desk draws at the right width before the fetch comes back.
 */

import { useCallback, useEffect, useState, type PointerEvent as ReactPointerEvent } from 'react';

import { usePreferences } from '../shell/preferencesContext';

const KEY = 'taxi.console.deskWidth';

/** The width the desk had before it could be changed, and the least it may be. */
export const DEFAULT_WIDTH = 900;

/** Below this the map is a postage stamp, and a dispatcher who wants none should press 1. */
const MAP_AT_LEAST = 360;

/** The desk's own padding and gap either side of the divider, which the map cannot use. */
const CHROME = 24 + 12;

function remembered(): number {
  try {
    const stored = Number(globalThis.localStorage?.getItem(KEY));
    return Number.isFinite(stored) && stored >= DEFAULT_WIDTH ? stored : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function remember(width: number): void {
  try {
    if (width === DEFAULT_WIDTH) globalThis.localStorage?.removeItem(KEY);
    else globalThis.localStorage?.setItem(KEY, String(width));
  } catch {
    // Storage refused: the width lasts the session, which is most of what matters.
  }
}

function clamp(width: number): number {
  const most = Math.max(DEFAULT_WIDTH, globalThis.innerWidth - MAP_AT_LEAST - CHROME);

  return Math.min(most, Math.max(DEFAULT_WIDTH, Math.round(width)));
}

export interface WorkingWidth {
  width: number;
  /** For the divider's `onPointerDown`: the drag follows the pointer until it lifts. */
  startDrag: (event: ReactPointerEvent<HTMLElement>) => void;
  /** Back to the width the desk ships with. The divider's double-click. */
  reset: () => void;
  /** For the divider's `onKeyDown`: the arrow keys move it, so it is not mouse-only. */
  nudge: (by: number) => void;
}

export function useWorkingWidth(): WorkingWidth {
  const [width, setWidth] = useState(remembered);
  const { save } = usePreferences();

  /** Kept for this PC and for this person: the default is "no choice", not a number. */
  const keep = useCallback(
    (next: number) => {
      remember(next);
      save({ deskWidth: next === DEFAULT_WIDTH ? null : next });
    },
    [save],
  );

  // A narrower window than the remembered width would push the map off the screen.
  useEffect(() => {
    const fit = () => setWidth((current) => clamp(current));

    globalThis.addEventListener('resize', fit);

    return () => globalThis.removeEventListener('resize', fit);
  }, []);

  const startDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;

    const handle = event.currentTarget;
    const startX = event.clientX;
    let startWidth = 0;
    let latest = 0;

    setWidth((current) => {
      startWidth = current;
      latest = current;
      return current;
    });

    // Captured, so the map's canvas underneath never sees the drag and the pointer may
    // leave the six-pixel handle without ending it.
    handle.setPointerCapture(event.pointerId);

    const move = (e: PointerEvent) => {
      latest = clamp(startWidth + (e.clientX - startX));
      setWidth(latest);
    };

    const stop = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', stop);
      handle.removeEventListener('pointercancel', stop);
      keep(latest);
    };

    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', stop);
    handle.addEventListener('pointercancel', stop);

    event.preventDefault();
  }, [keep]);

  const reset = useCallback(() => {
    setWidth(DEFAULT_WIDTH);
    keep(DEFAULT_WIDTH);
  }, [keep]);

  const nudge = useCallback((by: number) => {
    setWidth((current) => {
      const next = clamp(current + by);
      keep(next);
      return next;
    });
  }, [keep]);

  return { width, startDrag, reset, nudge };
}
